import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Package, Smartphone, FileText, Gem, StickyNote } from 'lucide-react';

interface UnbeddedPropertyDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (notes: string) => void;
  clientName: string;
  existingNotes?: string;
}

const UnbeddedPropertyDialog: React.FC<UnbeddedPropertyDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  clientName,
  existingNotes
}) => {
  const [propertyLocation, setPropertyLocation] = useState('');
  const [electronics, setElectronics] = useState('');
  const [documents, setDocuments] = useState('');
  const [valuables, setValuables] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');

  // Parse existing notes on open
  React.useEffect(() => {
    if (isOpen && existingNotes) {
      try {
        const parsed = JSON.parse(existingNotes);
        setPropertyLocation(parsed.propertyLocation || '');
        setElectronics(parsed.electronics || '');
        setDocuments(parsed.documents || '');
        setValuables(parsed.valuables || '');
        setAdditionalNotes(parsed.additionalNotes || '');
      } catch {
        setAdditionalNotes(existingNotes);
      }
    } else if (isOpen) {
      setPropertyLocation('');
      setElectronics('');
      setDocuments('');
      setValuables('');
      setAdditionalNotes('');
    }
  }, [isOpen, existingNotes]);

  const handleConfirm = () => {
    const notes = JSON.stringify({
      propertyLocation,
      electronics,
      documents,
      valuables,
      additionalNotes
    });
    onConfirm(notes);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-destructive" />
            Record Property — {clientName}
          </DialogTitle>
          <DialogDescription>
            This client is being marked as Unbedded. Record where their belongings are stored.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5 text-sm font-medium">
              <Package className="h-3.5 w-3.5 text-muted-foreground" />
              Property Location
            </Label>
            <Input
              placeholder="e.g., Storage Room B, Bin 14"
              value={propertyLocation}
              onChange={(e) => setPropertyLocation(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5 text-sm font-medium">
              <Smartphone className="h-3.5 w-3.5 text-muted-foreground" />
              Electronics
            </Label>
            <Textarea
              placeholder="e.g., 1x phone, 1x tablet"
              value={electronics}
              onChange={(e) => setElectronics(e.target.value)}
              rows={2}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5 text-sm font-medium">
              <FileText className="h-3.5 w-3.5 text-muted-foreground" />
              Documents
            </Label>
            <Textarea
              placeholder="e.g., ID, birth certificate in manila envelope"
              value={documents}
              onChange={(e) => setDocuments(e.target.value)}
              rows={2}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5 text-sm font-medium">
              <Gem className="h-3.5 w-3.5 text-muted-foreground" />
              Other Valuables
            </Label>
            <Textarea
              placeholder="e.g., Watch, ring, wallet"
              value={valuables}
              onChange={(e) => setValuables(e.target.value)}
              rows={2}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5 text-sm font-medium">
              <StickyNote className="h-3.5 w-3.5 text-muted-foreground" />
              Additional Notes
            </Label>
            <Textarea
              placeholder="Any other relevant information..."
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={handleConfirm}>
            Confirm Unbedded
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UnbeddedPropertyDialog;
